import { getApiBaseUrl } from './ApiUtils';
// CandidateAnalysisService.js
// Comprehensive candidate analysis service based on analysis rules
// Implements neutral profiling with psychometric labels and competency scoring

import firebase from '../config';

// Readiness Level Mapping
const getReadinessLevel = (percentage) => {
  if (percentage >= 90) return 5;
  if (percentage >= 80) return 4;
  if (percentage >= 70) return 3;
  if (percentage >= 50) return 2;
  return 1;
};

// Psychometric Label Determination
const determinePsychometricLabels = (psychometricScores) => {
  const labels = {};
  
  // Debug: Log the actual keys in psychometricScores
  // console.log('Available psychometric score keys:', Object.keys(psychometricScores));
  // console.log('Psychometric scores object:', psychometricScores);
  
  // A) Problem Solving & Logical Reasoning (Section 1)
  const problemSolvingPercentage = psychometricScores['Problem-Solving & Logical Reasoning']?.percentage || 0;
  // console.log('Problem Solving percentage:', problemSolvingPercentage);
  
  labels['Problem-Solving & Logical Reasoning'] = {
    label: problemSolvingPercentage >= 70 ? 'Structured Analyst' : 'Holistic/Intuitive Thinker',
    definition: problemSolvingPercentage >= 70 
      ? 'Uses stepwise, framework-driven reasoning. Reliable for structured problem-solving.'
      : 'Connects ideas broadly, prefers insight-driven solutions.',
    confidence: problemSolvingPercentage >= 70 ? problemSolvingPercentage : (100 - problemSolvingPercentage)
  };

  // B) Learning Orientation - Scope Preference (Section 2)
  const learningPercentage = psychometricScores['Learning Orientation']?.percentage || 0;
  // console.log('Learning Orientation percentage:', learningPercentage);
  
  labels['Learning Orientation'] = {
    label: learningPercentage >= 70 ? 'Depth Learner' : 'Breadth Learner',
    definition: learningPercentage >= 70 
      ? 'Dives deep into fewer subjects, building mastery.'
      : 'Explores widely across topics, connects diverse concepts.',
    confidence: learningPercentage >= 70 ? learningPercentage : (100 - learningPercentage)
  };

  // C) Collaboration & Communication (Section 3)
  const collaborationPercentage = psychometricScores['Collaboration, Coachability & Communication']?.percentage || 0;
  // console.log('Collaboration percentage:', collaborationPercentage);
  
  labels['Collaboration, Coachability & Communication'] = {
    label: collaborationPercentage >= 70 ? 'Open Collaborator' : 'Reserved Autonomist',
    definition: collaborationPercentage >= 70 
      ? 'Proactive communicator, invites feedback, transparent.'
      : 'Prefers autonomy, shares updates thoughtfully but less often.',
    confidence: collaborationPercentage >= 70 ? collaborationPercentage : (100 - collaborationPercentage)
  };

  // D) AI Orientation (Section 4)
  const aiPercentage = psychometricScores['AI Orientation']?.percentage || 0;
  // console.log('AI Orientation percentage:', aiPercentage);
  
  labels['AI Orientation'] = {
    label: aiPercentage >= 70 ? 'AI Innovator' : 'AI Adopter',
    definition: aiPercentage >= 70 
      ? 'Experiments early with AI, integrates into workflows.'
      : 'Prefers structured adoption of proven AI practices.',
    confidence: aiPercentage >= 70 ? aiPercentage : (100 - aiPercentage)
  };

  // E) Learning Orientation - Initiative Style (Derived from Section 2 + Section 3)
  const isDriverLearner = learningPercentage >= 80 && collaborationPercentage >= 70;
  labels['Learning Orientation (Initiative)'] = {
    label: isDriverLearner ? 'Driver Learner' : 'Independent Learner',
    definition: isDriverLearner 
      ? 'Mobilizes peers, initiates team learning sprints.'
      : 'Learns autonomously, prefers self-direction.',
    confidence: isDriverLearner ? Math.min(learningPercentage, collaborationPercentage) : 
                Math.max(100 - learningPercentage, 100 - collaborationPercentage)
  };

  return labels;
};

// Generate interpretations based on scores
const generateInterpretation = (subCompetency, percentage, readinessLevel) => {
  const interpretations = {
    // MCQ Interpretations
    'Java': {
      3: 'Candidate shows developing readiness in Java fundamentals; able to handle core concepts, may need more fluency in advanced syntax.',
      2: 'Demonstrates partial familiarity with Java basics; approaches some problems effectively but may need structured learning in core concepts.',
      1: 'Shows beginning awareness of Java; would benefit from foundational training in syntax and object-oriented principles.'
    },
    'DSA': {
      4: 'Strong algorithmic thinking with systematic approach to data structures; demonstrates efficient problem-solving patterns.',
      3: 'Good grasp of common algorithms and data structures; applies structured reasoning in most scenarios.',
      2: 'Demonstrates partial familiarity with algorithms; approaches some problems effectively but may rely on intuitive reasoning over structured methods.',
      1: 'Beginning understanding of algorithmic concepts; would benefit from systematic training in data structures and algorithm design.'
    },
    'MySQL': {
      4: 'Advanced database querying skills with optimization awareness; comfortable with complex joins and schema design.',
      3: 'Solid understanding of MySQL fundamentals with good query construction abilities.',
      2: 'Understands basic queries and joins; more comfort with optimization and schema design will help.',
      1: 'Basic familiarity with MySQL syntax; needs structured learning in query design and database concepts.'
    },
    'OOPs': {
      4: 'Demonstrates strong comfort with object-oriented principles, inheritance, and encapsulation.',
      3: 'Good understanding of OOP concepts with practical application abilities.',
      2: 'Developing grasp of object-oriented principles; can apply basic concepts but needs reinforcement in advanced patterns.',
      1: 'Beginning understanding of OOP; would benefit from foundational training in object-oriented design.'
    },
    // Logical Reasoning Interpretations
    'Problem Solving': {
      4: 'Consistently applies structured reasoning with systematic problem decomposition.',
      3: 'Candidate applies structured reasoning in most cases but occasionally leans on intuitive leaps.',
      2: 'Shows problem-solving capability with mixed approaches; benefits from more structured frameworks.',
      1: 'Developing problem-solving skills; would benefit from training in systematic analysis methods.'
    },
    'Process': {
      4: 'Consistently follows logical steps; indicates comfort with structured frameworks.',
      3: 'Good process orientation with systematic approach to workflow management.',
      2: 'Shows process awareness but may need guidance in complex workflow scenarios.',
      1: 'Developing process orientation; benefits from training in systematic workflow management.'
    },
    'Product': {
      4: 'Strong product thinking with comprehensive user and system impact analysis.',
      3: 'Good product reasoning with awareness of user needs and system implications.',
      2: 'Candidate connects reasoning to product scenarios at a basic level; may need guidance to think across user and system impact.',
      1: 'Beginning product awareness; needs training in user-centric thinking and system design.'
    }
  };

  const competencyInterpretations = interpretations[subCompetency];
  if (competencyInterpretations && competencyInterpretations[readinessLevel]) {
    return competencyInterpretations[readinessLevel];
  }

  // Default interpretations based on readiness level
  const defaultInterpretations = {
    5: `Demonstrates exceptional readiness in ${subCompetency} with advanced capabilities.`,
    4: `Shows strong readiness in ${subCompetency} with consistent performance.`,
    3: `Displays developing readiness in ${subCompetency} with good foundational understanding.`,
    2: `Shows partial readiness in ${subCompetency}; benefits from targeted development.`,
    1: `Beginning readiness in ${subCompetency}; requires foundational training and support.`
  };

  return defaultInterpretations[readinessLevel] || `Assessment shows ${percentage}% performance in ${subCompetency}.`;
};

// Calculate domain scores
const calculateDomainScores = (mcqScores, logicalScores, psychometricScores) => {
  // Technical Domain = MCQ technical + Psychometric AI_Orientation
  const technicalMcqAvg = mcqScores.length > 0 
    ? mcqScores.reduce((sum, score) => sum + score.percentage, 0) / mcqScores.length 
    : 0;
  const aiOrientationScore = psychometricScores.AI_Orientation?.percentage || 0;
  const technicalPercentage = Math.round((technicalMcqAvg + aiOrientationScore) / 2);

  // Functional Domain = Logical Reasoning + Psychometric ProblemSolving_Logical + LearningOrientation
  const logicalAvg = logicalScores.length > 0 
    ? logicalScores.reduce((sum, score) => sum + score.percentage, 0) / logicalScores.length 
    : 0;
  const problemSolvingScore = psychometricScores.ProblemSolving_Logical?.percentage || 0;
  const learningScore = psychometricScores.LearningOrientation?.percentage || 0;
  const functionalPercentage = Math.round((logicalAvg + problemSolvingScore + learningScore) / 3);

  // Behavioral Domain = Psychometric Collaboration_Coachability_Communication
  const behavioralPercentage = psychometricScores.Collaboration_Coachability_Communication?.percentage || 0;

  return {
    technical: {
      percentage: technicalPercentage,
      readinessLevel: getReadinessLevel(technicalPercentage),
      calculation: `(MCQ Avg: ${Math.round(technicalMcqAvg)}% + AI Orientation: ${aiOrientationScore}%) / 2`
    },
    functional: {
      percentage: functionalPercentage,
      readinessLevel: getReadinessLevel(functionalPercentage),
      calculation: `(Logical Avg: ${Math.round(logicalAvg)}% + Problem Solving: ${problemSolvingScore}% + Learning: ${learningScore}%) / 3`
    },
    behavioral: {
      percentage: behavioralPercentage,
      readinessLevel: getReadinessLevel(behavioralPercentage),
      calculation: `Collaboration & Communication: ${behavioralPercentage}%`
    }
  };
};

// Calculate weighted overall score
const calculateWeightedOverall = (mcqAvg, logicalAvg, psychometricAvg) => {
  const weightedPercentage = Math.round((mcqAvg * 0.30) + (logicalAvg * 0.30) + (psychometricAvg * 0.40));
  return {
    percentage: weightedPercentage,
    readinessLevel: getReadinessLevel(weightedPercentage),
    calculation: `(MCQ: ${Math.round(mcqAvg)}% × 0.30) + (Logical: ${Math.round(logicalAvg)}% × 0.30) + (Psychometric: ${Math.round(psychometricAvg)}% × 0.40)`
  };
};

// Generate suggested interview questions based on labels
const generateInterviewQuestions = (labels) => {
  const questionMap = {
    'Structured Analyst': [
      'Can you describe a problem where you solved it step by step? How did you decide on the order of steps?',
      'Walk me through your approach to breaking down a complex technical challenge.',
      'How do you ensure your problem-solving process is systematic and repeatable?'
    ],
    'Holistic/Intuitive Thinker': [
      'Tell me about a time when you had to connect seemingly unrelated concepts to solve a problem.',
      'How do you approach problems when the solution isn\'t immediately obvious?',
      'Describe a situation where your intuition led you to an innovative solution.'
    ],
    'Depth Learner': [
      'Tell me about a technology or concept you\'ve mastered deeply. How did you approach that learning?',
      'How do you decide when you\'ve learned enough about a topic to move on?',
      'Describe your process for becoming an expert in a new technical area.'
    ],
    'Breadth Learner': [
      'When learning a new technology, how do you decide what to explore broadly versus what to study deeply?',
      'How do you stay current with multiple technologies or domains?',
      'Tell me about a time when your broad knowledge helped you solve a specific problem.'
    ],
    'Driver Learner': [
      'Describe a time when you organized a learning initiative for your team.',
      'How do you help others learn new concepts or technologies?',
      'Tell me about a situation where you took the lead in a group learning scenario.'
    ],
    'Independent Learner': [
      'Tell me about a time you had to learn something quickly on your own. How did you structure your approach?',
      'How do you stay motivated when learning challenging concepts independently?',
      'Describe your personal learning process for acquiring new technical skills.'
    ],
    'Open Collaborator': [
      'How do you ensure effective communication in a team setting?',
      'Tell me about a time when you actively sought feedback to improve your work.',
      'Describe how you approach sharing knowledge with team members.'
    ],
    'Reserved Autonomist': [
      'When working independently, how do you ensure your team stays informed about your progress?',
      'How do you balance autonomy with team collaboration requirements?',
      'Tell me about a time when you had to adapt your communication style for better team integration.'
    ],
    'AI Innovator': [
      'Can you share an example of how you experimented with AI tools in your work or studies? What did you discover?',
      'How do you stay current with AI developments in your field?',
      'Describe a situation where you integrated AI capabilities into a project or workflow.'
    ],
    'AI Adopter': [
      'How do you evaluate new AI tools before incorporating them into your workflow?',
      'Tell me about your approach to learning established AI practices.',
      'Describe how you ensure reliable implementation of AI solutions.'
    ]
  };

  return Object.entries(labels).map(([key, labelData]) => ({
    label: labelData.label,
    questions: questionMap[labelData.label] || [
      `Tell me about your experience with ${labelData.label.toLowerCase()} approaches.`,
      `How do you apply ${labelData.label.toLowerCase()} principles in your work?`,
      `Describe a situation where your ${labelData.label.toLowerCase()} style was particularly effective.`
    ]
  }));
};

// Function to detect exam type based on section titles
const detectExamTypeFromSections = (examData) => {
  if (!examData || !examData.sections) {
    return 'Unknown';
  }

  const sectionTitles = examData.sections.map(section => (section.t || '').toLowerCase());
  const allTitles = sectionTitles.join(' ');

  // Check for MCQ sections (Java & OOPs, DSA, MySQL)
  const mcqKeywords = ['java', 'oop', 'dsa', 'mysql', 'sql'];
  const hasMcqSections = mcqKeywords.some(keyword => allTitles.includes(keyword));
  
  // Check for Logical sections (Coding Review)
  const hasLogicalSections = allTitles.includes('coding review') || allTitles.includes('code review');
  
  // Check for Psychometric sections
  const psychometricKeywords = [
    'problem-solving', 'logical reasoning', 'learning orientation', 
    'collaboration', 'coachability', 'communication', 'ai orientation'
  ];
  const hasPsychometricSections = psychometricKeywords.some(keyword => allTitles.includes(keyword));

  // Determine exam type based on section content
  if (hasMcqSections && !hasLogicalSections && !hasPsychometricSections) {
    return 'MCQ';
  } else if (hasLogicalSections && !hasMcqSections && !hasPsychometricSections) {
    return 'Logical';
  } else if (hasPsychometricSections && !hasMcqSections && !hasLogicalSections) {
    return 'Psychometric';
  } else if (hasMcqSections && hasLogicalSections && !hasPsychometricSections) {
    // Mixed MCQ + Logical - prioritize based on majority
    const mcqCount = sectionTitles.filter(title => mcqKeywords.some(keyword => title.includes(keyword))).length;
    const logicalCount = sectionTitles.filter(title => title.includes('coding review') || title.includes('code review')).length;
    return logicalCount >= mcqCount ? 'Logical' : 'MCQ';
  } else if (hasPsychometricSections) {
    // If psychometric sections are present, it's likely a psychometric exam
    return 'Psychometric';
  } else {
    // Default fallback - try to determine from first section
    const firstSection = sectionTitles[0] || '';
    if (mcqKeywords.some(keyword => firstSection.includes(keyword))) {
      return 'MCQ';
    } else if (firstSection.includes('coding review') || firstSection.includes('code review')) {
      return 'Logical';
    } else if (psychometricKeywords.some(keyword => firstSection.includes(keyword))) {
      return 'Psychometric';
    }
    return 'Unknown';
  }
};

// Fetch assessment data from Firebase
const fetchAssessmentDataFromFirebase = async (email, examCodes) => {
  try {
    
    const assessmentData = {
      candidateEmail: email,
      examCodes: examCodes,
      mcqResults: {},
      logicalResults: {},
      psychometricResults: {},
      examDates: [],
      candidateName: ''
    };

    // Process exams and detect type based on section titles
    for (let i = 0; i < examCodes.length; i++) {
      const examCode = examCodes[i];
      
      // Detect exam type based on section titles
      let examType = 'Unknown';
      
      // First fetch exam structure to detect type
      const examRef = firebase.database().ref(`TestPapers/${examCode}`);
      const examSnapshot = await examRef.once('value');
      
      if (examSnapshot.exists()) {
        const examData = examSnapshot.val();
        examType = detectExamTypeFromSections(examData);
      } else {
        continue;
      }
      
      // Skip if exam type is unknown or if we already have this type
      if (examType === 'Unknown') {
        continue;
      }
      
      // Check if we already have this exam type (to avoid duplicates)
      const existingTypes = [];
      if (Object.keys(assessmentData.mcqResults).length > 0) existingTypes.push('MCQ');
      if (Object.keys(assessmentData.logicalResults).length > 0) existingTypes.push('Logical');
      if (Object.keys(assessmentData.psychometricResults).length > 0) existingTypes.push('Psychometric');
      
      if (existingTypes.includes(examType)) {
        continue;
      }

      try {
        // Fetch exam result data
        const resultRef = firebase.database().ref(`Exam_results/${examCode}/${email}`);
        const resultSnapshot = await resultRef.once('value');
        
        if (!resultSnapshot.exists()) {
          continue;
        }

        const resultData = resultSnapshot.val();
        
        // Store candidate name from first exam
        if (!assessmentData.candidateName && resultData.name) {
          assessmentData.candidateName = resultData.name;
        }

        // Store exam date
        if (resultData.submittedAt) {
          const examDate = new Date(resultData.submittedAt).toLocaleString();
          assessmentData.examDates.push(`${examCode}: ${examDate}`);
        }

        // Use the exam data we already fetched for type detection
        const examData = examSnapshot.val();        
        
        
        // Process AI evaluation data with section-based categorization
        if (resultData.aiEvaluation && resultData.aiEvaluation.evaluations) {
          const evaluations = resultData.aiEvaluation.evaluations;
          
          // Group questions by section for proper calculation
          const sectionResults = {};
          
          // First pass: collect all questions by section
          for (const [questionId, evaluation] of Object.entries(evaluations)) {
            const score = evaluation.score || 0;
            const maxScore = evaluation.maxScore || 1;
            
            // Find the question in exam structure to get section info
            let sectionTitle = '';
            let sectionWeight = 0;
            
            if (examData.sections) {
              for (const section of examData.sections) {
                if (section.q) {
                  for (const question of section.q) {
                    if (question.id === questionId) {
                      sectionTitle = section.t || '';
                      sectionWeight = section.w || 0;
                      break;
                    }
                  }
                }
                if (sectionTitle) break;
              }
            }
            
            // Initialize section if not exists
            if (!sectionResults[sectionTitle]) {
              sectionResults[sectionTitle] = {
                correct: 0,
                totalQuestions: 0,
                totalMaxScore: 0,
                weight: sectionWeight,
                examType: examType
              };
            }
            
            // Add to section totals - FIXED: All exam types now use same logic
            if (examType === 'MCQ' || examType === 'Logical' || examType === 'Psychometric') {
              // For all exam types: count questions answered correctly (score > 0)
              sectionResults[sectionTitle].correct += (score > 0) ? 1 : 0;
            }
            sectionResults[sectionTitle].totalQuestions += 1; // Count actual questions
            sectionResults[sectionTitle].totalMaxScore += maxScore; // Sum of max scores
          }
          
          // Second pass: categorize sections and add to appropriate results
          for (const [sectionTitle, sectionData] of Object.entries(sectionResults)) {
            const competency = categorizeSectionByTitle(sectionTitle, examType);
            
            if (examType === 'MCQ') {
              if (!assessmentData.mcqResults[competency]) {
                assessmentData.mcqResults[competency] = { correct: 0, total: 0, maxScore: 0 };
              }
              // For MCQ: x/y where x = correct answers, y = total questions
              assessmentData.mcqResults[competency].correct += sectionData.correct;
              assessmentData.mcqResults[competency].total += sectionData.totalQuestions;
              assessmentData.mcqResults[competency].maxScore += sectionData.totalMaxScore;
            } else if (examType === 'Logical') {
              if (!assessmentData.logicalResults[competency]) {
                assessmentData.logicalResults[competency] = { correct: 0, total: 0, maxScore: 0 };
              }
              // For Logical: x/y where x = correct answers, y = total questions
              assessmentData.logicalResults[competency].correct += sectionData.correct;
              assessmentData.logicalResults[competency].total += sectionData.totalQuestions;
              assessmentData.logicalResults[competency].maxScore += sectionData.totalMaxScore;
            } else if (examType === 'Psychometric') {
              if (!assessmentData.psychometricResults[competency]) {
                assessmentData.psychometricResults[competency] = { correct: 0, total: 0 };
              }
              // For Psychometric: FIXED - now uses same format as MCQ/Logical (correct/total)
              assessmentData.psychometricResults[competency].correct += sectionData.correct;
              assessmentData.psychometricResults[competency].total += sectionData.totalQuestions;
            }
          }
        }

        // Handle failed AI evaluations or missing AI evaluation
        // Check if AI evaluation failed or is missing
        const hasAiEvaluation = resultData.aiEvaluation && resultData.aiEvaluation.evaluations;
        const hasAiError = resultData.aiEvaluation && (resultData.aiEvaluation.error || resultData.aiEvaluation.status === 'failed');
        const isFallbackEvaluation = resultData.aiEvaluation && resultData.aiEvaluation.fallback;
        
        // Additional checks for different failure patterns
        const hasOnlyErrorStatus = resultData.aiEvaluation && 
          resultData.aiEvaluation.error && 
          resultData.aiEvaluation.status && 
          !resultData.aiEvaluation.evaluations;
        
        const hasEmptyEvaluations = resultData.aiEvaluation && 
          resultData.aiEvaluation.evaluations && 
          Object.keys(resultData.aiEvaluation.evaluations).length === 0;
        
        
        if (!hasAiEvaluation || hasAiError || hasOnlyErrorStatus || hasEmptyEvaluations) {
          // Check if fallback evaluation already exists
          if (isFallbackEvaluation) {
            
            // Process existing fallback evaluation data using the same logic as AI evaluation
            const evaluations = resultData.aiEvaluation.evaluations;
            
            // Group questions by section for proper calculation
            const sectionResults = {};
            
            // First pass: collect all questions by section
            for (const [questionId, evaluation] of Object.entries(evaluations)) {
              const score = evaluation.score || 0;
              const maxScore = evaluation.maxScore || 1;
              
              // Find the question in exam structure to get section info
              let sectionTitle = '';
              let sectionWeight = 0;
              
              if (examData.sections) {
                for (const section of examData.sections) {
                  if (section.q) {
                    for (const question of section.q) {
                      if (question.id === questionId) {
                        sectionTitle = section.t || '';
                        sectionWeight = section.w || 0;
                        break;
                      }
                    }
                  }
                  if (sectionTitle) break;
                }
              }
              
              // Initialize section if not exists
              if (!sectionResults[sectionTitle]) {
                sectionResults[sectionTitle] = {
                  correct: 0,
                  totalQuestions: 0,
                  totalMaxScore: 0,
                  weight: sectionWeight,
                  examType: examType
                };
              }
              
              // Add to section totals - FIXED: All exam types now use same logic
              if (examType === 'MCQ' || examType === 'Logical' || examType === 'Psychometric') {
                // For all exam types: count questions answered correctly (score > 0)
                sectionResults[sectionTitle].correct += (score > 0) ? 1 : 0;
              }
              sectionResults[sectionTitle].totalQuestions += 1; // Count actual questions
              sectionResults[sectionTitle].totalMaxScore += maxScore; // Sum of max scores
            }
            
            // Second pass: categorize sections and add to appropriate results
            for (const [sectionTitle, sectionData] of Object.entries(sectionResults)) {
              const competency = categorizeSectionByTitle(sectionTitle, examType);
              
              if (examType === 'MCQ') {
                if (!assessmentData.mcqResults[competency]) {
                  assessmentData.mcqResults[competency] = { correct: 0, total: 0, maxScore: 0 };
                }
                // For MCQ: x/y where x = correct answers, y = total questions
                assessmentData.mcqResults[competency].correct += sectionData.correct;
                assessmentData.mcqResults[competency].total += sectionData.totalQuestions;
                assessmentData.mcqResults[competency].maxScore += sectionData.totalMaxScore;
              } else if (examType === 'Logical') {
                if (!assessmentData.logicalResults[competency]) {
                  assessmentData.logicalResults[competency] = { correct: 0, total: 0, maxScore: 0 };
                }
                // For Logical: x/y where x = correct answers, y = total questions
                assessmentData.logicalResults[competency].correct += sectionData.correct;
                assessmentData.logicalResults[competency].total += sectionData.totalQuestions;
                assessmentData.logicalResults[competency].maxScore += sectionData.totalMaxScore;
              } else if (examType === 'Psychometric') {
                if (!assessmentData.psychometricResults[competency]) {
                  assessmentData.psychometricResults[competency] = { correct: 0, total: 0 };
                }
                // For Psychometric: FIXED - now uses same format as MCQ/Logical (correct/total)
                assessmentData.psychometricResults[competency].correct += sectionData.correct;
                assessmentData.psychometricResults[competency].total += sectionData.totalQuestions;
              }
            }
          } else {
            
            // Create fallback evaluation from basic answers using AIEvaluationService
            const fallbackEvaluation = await createFallbackEvaluation(resultData, examData, examType);
            
            if (fallbackEvaluation) {
              
              // Store the fallback evaluation back to Firebase (same pattern as AI evaluation)
              try {
                const submissionRef = firebase.database().ref(`Exam_results/${examCode}/${email}`);
                await submissionRef.update({
                  aiEvaluation: fallbackEvaluation
                });
              } catch (storageError) {
                console.error(`❌ Failed to store fallback evaluation for ${examCode}:`, storageError);
                // Continue with analysis even if storage fails
              }
              
              // Process the fallback evaluation data using the SAME logic as successful AI evaluations
              const evaluations = fallbackEvaluation.evaluations;
              
              // Group questions by section for proper calculation - SAME as successful AI evaluation
              const sectionResults = {};
              
              // First pass: collect all questions by section
              for (const [questionId, evaluation] of Object.entries(evaluations)) {
                const score = evaluation.score || 0;
                const maxScore = evaluation.maxScore || 1;
                
                // Find the question in exam structure to get section info
                let sectionTitle = '';
                let sectionWeight = 0;
                
                if (examData.sections) {
                  for (const section of examData.sections) {
                    if (section.q) {
                      for (const question of section.q) {
                        if (question.id === questionId) {
                          sectionTitle = section.t || '';
                          sectionWeight = section.w || 0;
                          break;
                        }
                      }
                    }
                    if (sectionTitle) break;
                  }
                }
                
                // Initialize section if not exists
                if (!sectionResults[sectionTitle]) {
                  sectionResults[sectionTitle] = {
                    correct: 0,
                    totalQuestions: 0,
                    totalMaxScore: 0,
                    weight: sectionWeight,
                    examType: examType
                  };
                }
                
                // Add to section totals - EXACT SAME logic as successful AI evaluation
                if (examType === 'MCQ' || examType === 'Logical' || examType === 'Psychometric') {
                  // For all exam types: count questions answered correctly (score > 0)
                  sectionResults[sectionTitle].correct += (score > 0) ? 1 : 0;
                }
                sectionResults[sectionTitle].totalQuestions += 1; // Count actual questions
                sectionResults[sectionTitle].totalMaxScore += maxScore; // Sum of max scores
              }
              
              // Second pass: categorize sections and add to appropriate results - SAME as successful AI evaluation
              for (const [sectionTitle, sectionData] of Object.entries(sectionResults)) {
                const competency = categorizeSectionByTitle(sectionTitle, examType);
                
                if (examType === 'MCQ') {
                  if (!assessmentData.mcqResults[competency]) {
                    assessmentData.mcqResults[competency] = { correct: 0, total: 0, maxScore: 0 };
                  }
                  // For MCQ: x/y where x = correct answers, y = total questions
                  assessmentData.mcqResults[competency].correct += sectionData.correct;
                  assessmentData.mcqResults[competency].total += sectionData.totalQuestions;
                  assessmentData.mcqResults[competency].maxScore += sectionData.totalMaxScore;
                } else if (examType === 'Logical') {
                  if (!assessmentData.logicalResults[competency]) {
                    assessmentData.logicalResults[competency] = { correct: 0, total: 0, maxScore: 0 };
                  }
                  // For Logical: x/y where x = correct answers, y = total questions
                  assessmentData.logicalResults[competency].correct += sectionData.correct;
                  assessmentData.logicalResults[competency].total += sectionData.totalQuestions;
                  assessmentData.logicalResults[competency].maxScore += sectionData.totalMaxScore;
                } else if (examType === 'Psychometric') {
                  if (!assessmentData.psychometricResults[competency]) {
                    assessmentData.psychometricResults[competency] = { correct: 0, total: 0 };
                  }
                  // For Psychometric: FIXED - now uses same format as MCQ/Logical (correct/total)
                  assessmentData.psychometricResults[competency].correct += sectionData.correct;
                  assessmentData.psychometricResults[competency].total += sectionData.totalQuestions;
                }
              }
            }
          }
        } else if (!resultData.aiEvaluation && resultData.answers) {
          console.log(`📝 Processing basic answers for ${examCode} - AI evaluation not available`);
        }

      } catch (examError) {
        console.error(`Error fetching data for exam ${examCode}:`, examError);
        continue;
      }
    }


    // If no data found, return empty structure
    if (Object.keys(assessmentData.mcqResults).length === 0 && 
        Object.keys(assessmentData.logicalResults).length === 0 && 
        Object.keys(assessmentData.psychometricResults).length === 0) {
      console.error(`❌ No assessment data found for ${email} in any of the provided exam codes: ${examCodes.join(', ')}`);
      throw new Error(`No assessment data found for ${email} in any of the provided exam codes: ${examCodes.join(', ')}`);
    }

    // Convert exam dates to string
    assessmentData.examDates = assessmentData.examDates.join('; ');

    return assessmentData;

  } catch (error) {
    console.error('Error fetching assessment data from Firebase:', error);
    throw error;
  }
};

// Categorize section by title according to the updated rules
const categorizeSectionByTitle = (sectionTitle, examType) => {
  const title = (sectionTitle || '').toLowerCase();
  
  if (examType === 'MCQ') {
    // MCQ Test sections: Java & OOPs, DSA, MySQL
    if (title.includes('java') && title.includes('oop')) {
      return 'Java & OOPs';
    } else if (title.includes('dsa')) {
      return 'DSA';
    } else if (title.includes('mysql')) {
      return 'MySQL';
    } else {
      return 'Java & OOPs'; // Default
    }
  } else if (examType === 'Logical') {
    // Logical Reasoning Test sections: Coding Review
    return 'Coding Review';
  } else if (examType === 'Psychometric') {
    // Psychometric Test sections: 4 specific competencies
    if (title.includes('problem-solving') && title.includes('logical') && title.includes('reasoning')) {
      return 'Problem-Solving & Logical Reasoning';
    } else if (title.includes('learning') && title.includes('orientation')) {
      return 'Learning Orientation';
    } else if (title.includes('collaboration') && title.includes('coachability') && title.includes('communication')) {
      return 'Collaboration, Coachability & Communication';
    } else if (title.includes('ai') && title.includes('orientation')) {
      return 'AI Orientation';
    } else {
      return 'Learning Orientation'; // Default
    }
  }
  
  return 'Others';
};

// Process assessment data according to analysis rules
const processAssessmentData = (data, email, role, examCodes) => {
  // Calculate MCQ scores - technical knowledge (Java & OOPs, DSA, MySQL)
  const mcqScores = Object.entries(data.mcqResults).map(([subCompetency, result]) => {
    // For MCQ: use correct questions / total questions
    const percentage = Math.round((result.correct / result.total) * 100);
    const readinessLevel = getReadinessLevel(percentage);
    return {
      subCompetency,
      raw: `${result.correct}/${result.total}`,
      percentage,
      readinessLevel,
      interpretation: generateInterpretation(subCompetency, percentage, readinessLevel)
    };
  });

  // Calculate Logical Reasoning scores - functional reasoning (Coding Review)
  const logicalScores = Object.entries(data.logicalResults).map(([subCompetency, result]) => {
    // For Logical: use correct questions / total questions
    const percentage = Math.round((result.correct / result.total) * 100);
    const readinessLevel = getReadinessLevel(percentage);
    return {
      subCompetency,
      raw: `${result.correct}/${result.total}`,
      percentage,
      readinessLevel,
      interpretation: generateInterpretation(subCompetency, percentage, readinessLevel)
    };
  });

  // Calculate Psychometric scores - FIXED: now uses same format as MCQ/Logical
  const psychometricScores = {};
  const psychometricScoresList = Object.entries(data.psychometricResults).map(([subCompetency, result]) => {
    // For Psychometric: FIXED - now use correct questions / total questions (same as MCQ/Logical)
    const percentage = Math.round((result.correct / result.total) * 100);
    const readinessLevel = getReadinessLevel(percentage);
    
    psychometricScores[subCompetency] = { percentage, readinessLevel };
    
    return {
      subCompetency,
      raw: `${result.correct}/${result.total}`,
      percentage,
      readinessLevel,
      interpretation: generatePsychometricInterpretation(subCompetency, percentage, readinessLevel)
    };
  });

  // Calculate averages
  const mcqAvg = mcqScores.length > 0 ? mcqScores.reduce((sum, score) => sum + score.percentage, 0) / mcqScores.length : 0;
  const logicalAvg = logicalScores.length > 0 ? logicalScores.reduce((sum, score) => sum + score.percentage, 0) / logicalScores.length : 0;
  const psychometricAvg = psychometricScoresList.length > 0 ? psychometricScoresList.reduce((sum, score) => sum + score.percentage, 0) / psychometricScoresList.length : 0;

  // Determine psychometric labels according to rules
  const psychometricLabels = determinePsychometricLabels(psychometricScores);

  // Calculate domain scores according to rules
  const domainScores = calculateDomainScores(mcqScores, logicalScores, psychometricScores);

  // Calculate weighted overall according to rules: MCQ 30% + Logical 30% + Psychometric 40%
  const weightedOverall = calculateWeightedOverall(mcqAvg, logicalAvg, psychometricAvg);

  // Generate strengths and focus areas
  const strengths = [...mcqScores, ...logicalScores, ...psychometricScoresList]
    .filter(score => score.readinessLevel >= 4)
    .map(score => score.subCompetency)
    .join(', ') || 'Developing across multiple areas';

  const focusAreas = [...mcqScores, ...logicalScores, ...psychometricScoresList]
    .filter(score => score.readinessLevel <= 3)
    .map(score => score.subCompetency)
    .join(', ') || 'Continue building on current strengths';

  // Generate interview questions based on labels
  const interviewQuestions = generateInterviewQuestions(psychometricLabels);

  // Generate competency interpreter text
  const competencyInterpreter = generateCompetencyInterpreter(mcqScores, logicalScores, psychometricScores, psychometricLabels);

  // Generate domain heatmap
  const domainHeatmap = generateDomainHeatmap(domainScores, psychometricLabels);

  // Generate overall candidate profiling
  const overallSummary = generateOverallSummary(data.candidateName || email.split('@')[0], domainScores, psychometricLabels, focusAreas);

  // Generate label glossary
  const labelGlossary = generateLabelGlossary();
  
  // Check if any fallback evaluations were used
  const hasFallbackEvaluations = [...mcqScores, ...logicalScores, ...psychometricScoresList]
    .some(score => score.fallback);
  

  // Return data matching exact CSV format
  return {
    candidateEmail: email,
    candidateName: data.candidateName || email.split('@')[0],
    role: role,
    examCodes: examCodes,
    totalAssessments: examCodes.length,
    
    // 1. Operational Details
    operationalDetails: {
      name: data.candidateName || email.split('@')[0] || 'Candidate',
      email: email,
      examCode: examCodes.join(', '),
      examDateTime: data.examDates,
      totalExams: examCodes.length,
      mcqPercentage: Math.round(mcqAvg),
      logicalPercentage: Math.round(logicalAvg),
      psychometricPercentage: Math.round(psychometricAvg)
    },

    // 2. Scores & Interpretations by Sub-Competency
    mcqScores: mcqScores,
    logicalScores: logicalScores,
    psychometricScores: psychometricScoresList,
    psychometricLabels: psychometricLabels,

    // 3. Domain Scores (for AI analysis)
    domainScores: domainScores,

    // 4. Strengths, Focus Areas, Training Ideas
    strengthsFocusTraining: {
      strengths: strengths,
      focusAreas: focusAreas,
      trainingIdeas: 'Structured competency development programs; collaborative learning initiatives; targeted skill enhancement workshops'
    },

    // 5. Competency Interpreter
    competencyInterpreter: competencyInterpreter,

    // 6. Domain Heatmap (with Confidence Scoring)
    domainHeatmap: domainHeatmap,

    // 7. Suggested Interview Questions (Label-Based)
    interviewQuestions: interviewQuestions,

    // 8. Overall Candidate Profiling
    overallSummary: overallSummary,

    // 9. Label Glossary for Managers
    labelGlossary: labelGlossary
  };
};

// Generate psychometric interpretation
const generatePsychometricInterpretation = (subCompetency, percentage, readinessLevel) => {
  const competencyName = subCompetency.replace(/_/g, ' ').toLowerCase();
  return `Candidate demonstrates ${readinessLevel >= 3 ? 'developing to strong' : 'emerging'} readiness in ${competencyName}.`;
};

// Generate competency interpreter text
const generateCompetencyInterpreter = (mcqScores, logicalScores, psychometricScores, psychometricLabels) => {
  const mcqAvg = mcqScores.length > 0 ? mcqScores.reduce((sum, score) => sum + score.percentage, 0) / mcqScores.length : 0;
  const logicalAvg = logicalScores.length > 0 ? logicalScores.reduce((sum, score) => sum + score.percentage, 0) / logicalScores.length : 0;
  
  // Get the labels using the new key names
  const aiStyle = psychometricLabels['AI Orientation'] || { label: 'AI Adopter' };
  const thinkingApproach = psychometricLabels['Problem-Solving & Logical Reasoning'] || { label: 'Holistic/Intuitive Thinker' };
  const learnerScope = psychometricLabels['Learning Orientation'] || { label: 'Breadth Learner' };
  const collaborationStyle = psychometricLabels['Collaboration, Coachability & Communication'] || { label: 'Reserved Autonomist' };
  const learnerInitiative = psychometricLabels['Learning Orientation (Initiative)'] || { label: 'Independent Learner' };
  
  const technicalProfile = `Technical Profile: Candidate shows ${mcqAvg >= 70 ? 'strong' : 'developing'} readiness in core coding and ${aiStyle.label === 'AI Innovator' ? 'natural engagement with AI tools' : 'structured approach to AI adoption'}. Their technical style aligns with a ${thinkingApproach.label}, preferring ${thinkingApproach.label === 'Structured Analyst' ? 'systematic approaches' : 'innovative solutions'} to coding challenges.`;
  
  const functionalProfile = `Functional Profile: Logical reasoning shows ${logicalAvg >= 70 ? 'consistency' : 'variability'} in process orientation, with a ${thinkingApproach.label} mindset. However, as a ${learnerScope.label}, the candidate ${learnerScope.label === 'Breadth Learner' ? 'samples across areas rather than going deep quickly' : 'builds deep expertise systematically'}. This may help in ${learnerScope.label === 'Breadth Learner' ? 'cross-functional problem-solving' : 'specialized technical challenges'} but can ${learnerScope.label === 'Breadth Learner' ? 'delay mastery' : 'limit cross-domain flexibility'}.`;
  
  const behavioralProfile = `Behavioral Profile: With a ${collaborationStyle.label} collaboration style and ${learnerInitiative.label} orientation, the candidate favors ${learnerInitiative.label === 'Driver Learner' ? 'team-oriented learning' : 'autonomy and self-driven work'}. They communicate ${collaborationStyle.label === 'Open Collaborator' ? 'proactively and frequently' : 'thoughtfully but less frequently'}, which managers should note for team-based projects.`;
  
  return { technicalProfile, functionalProfile, behavioralProfile };
};

// Generate domain heatmap
const generateDomainHeatmap = (domainScores, psychometricLabels) => {
  // Get the labels using the new key names
  const aiStyle = psychometricLabels['AI Orientation'] || { label: 'AI Adopter', confidence: 0 };
  const thinkingApproach = psychometricLabels['Problem-Solving & Logical Reasoning'] || { label: 'Holistic/Intuitive Thinker', confidence: 0 };
  const learnerScope = psychometricLabels['Learning Orientation'] || { label: 'Breadth Learner', confidence: 0 };
  const collaborationStyle = psychometricLabels['Collaboration, Coachability & Communication'] || { label: 'Reserved Autonomist', confidence: 0 };
  const learnerInitiative = psychometricLabels['Learning Orientation (Initiative)'] || { label: 'Independent Learner', confidence: 0 };
  
  return [
    {
      domain: 'Technical + Behavioral',
      indicators: `Java ${domainScores.technical.mcqAvg || 0}% (L${domainScores.technical.readinessLevel}), AI Orientation ${aiStyle.confidence}% (${aiStyle.label})`,
      interviewRecommendation: 'Explore coding under pressure and practical AI use',
      description: `Candidate leans ${aiStyle.confidence}% toward ${aiStyle.label} (with ${100 - aiStyle.confidence}% adopting tendencies). This means they ${aiStyle.label === 'AI Innovator' ? 'experiment early with AI tools and show strong adaptability' : 'prefer structured adoption patterns'}. Their problem-solving approach shows ${thinkingApproach.confidence}% ${thinkingApproach.label}, ${100 - thinkingApproach.confidence}% ${thinkingApproach.label === 'Structured Analyst' ? 'Holistic' : 'Structured Analyst'} tendencies, reflecting mostly ${thinkingApproach.label === 'Structured Analyst' ? 'stepwise reasoning with some intuitive inputs' : 'intuitive reasoning with some structured approaches'}.`
    },
    {
      domain: 'Logical + Behavioral',
      indicators: `Problem Solving ${domainScores.functional.logicalAvg || 0}% (L${domainScores.functional.readinessLevel}), Learning Orientation ${learnerScope.confidence}% (${learnerScope.label})`,
      interviewRecommendation: 'Ask how they move from broad exploration to focused depth',
      description: `Candidate demonstrates ${learnerScope.confidence}% ${learnerScope.label}, ${100 - learnerScope.confidence}% ${learnerScope.label === 'Breadth Learner' ? 'Depth' : 'Breadth'} tendencies — signaling preference for ${learnerScope.label === 'Breadth Learner' ? 'wide exploration before deep dives' : 'deep expertise before broad exploration'}. Initiative style leans ${learnerInitiative.label}, showing comfort with ${learnerInitiative.label === 'Driver Learner' ? 'team-oriented learning and peer mobilization' : 'self-directed learning, though not strongly mobilizing peers'}.`
    },
    {
      domain: 'Behavioral Only',
      indicators: `Collaboration ${collaborationStyle.confidence}% (${collaborationStyle.label})`,
      interviewRecommendation: 'Probe teamwork and communication preferences',
      description: `Candidate leans ${collaborationStyle.confidence}% toward ${collaborationStyle.label}, indicating a preference for ${collaborationStyle.label === 'Open Collaborator' ? 'proactive communication and frequent updates' : 'autonomy and thoughtful but less frequent updates'}. About ${100 - collaborationStyle.confidence}% of responses show ${collaborationStyle.label === 'Open Collaborator' ? 'autonomist' : 'collaborator'} tendencies, suggesting they may adapt to more ${collaborationStyle.label === 'Open Collaborator' ? 'independent' : 'open'} styles when required.`
    }
  ];
};

// Generate overall candidate profiling
const generateOverallSummary = (candidateName, domainScores, psychometricLabels, focusAreas) => {
  // Get the labels using the new key names
  const aiStyle = psychometricLabels['AI Orientation'] || { label: 'AI Adopter', confidence: 0 };
  const thinkingApproach = psychometricLabels['Problem-Solving & Logical Reasoning'] || { label: 'Holistic/Intuitive Thinker', confidence: 0 };
  const learnerScope = psychometricLabels['Learning Orientation'] || { label: 'Breadth Learner', confidence: 0 };
  const collaborationStyle = psychometricLabels['Collaboration, Coachability & Communication'] || { label: 'Reserved Autonomist', confidence: 0 };
  const learnerInitiative = psychometricLabels['Learning Orientation (Initiative)'] || { label: 'Independent Learner', confidence: 0 };
  
  return `${candidateName} demonstrates a ${domainScores.technical.readinessLevel >= 3 ? 'robust technical foundation with advanced capabilities' : 'developing technical competency with growth potential'} and exhibits ${aiStyle.label === 'AI Innovator' ? 'proactive engagement with emerging AI technologies' : 'methodical adoption of established AI frameworks'} (${aiStyle.label}, ${aiStyle.confidence}% confidence). Their ${thinkingApproach.label} cognitive approach (${thinkingApproach.confidence}% confidence) indicates a preference for ${thinkingApproach.label === 'Structured Analyst' ? 'systematic methodologies and structured problem-solving' : 'innovative solutions and creative approaches'}, while their ${learnerScope.label} learning orientation (${learnerScope.confidence}% confidence) suggests they ${learnerScope.label === 'Depth Learner' ? 'develop comprehensive expertise through focused study' : 'explore diverse domains before specializing'}. As an ${learnerInitiative.label} with ${collaborationStyle.label} communication style (${collaborationStyle.confidence}% confidence), they demonstrate ${learnerInitiative.label === 'Driver Learner' ? 'collaborative leadership and team-oriented development' : 'autonomous learning and self-directed growth, with communication that is'} ${collaborationStyle.label === 'Open Collaborator' ? 'transparent and frequent' : 'strategic and purposeful'}.\n\nThis professional profile indicates a candidate who excels in ${thinkingApproach.label === 'Structured Analyst' ? 'systematic development environments and structured technical challenges' : 'dynamic problem-solving scenarios and innovative project contexts'}, ${aiStyle.label === 'AI Innovator' ? 'cutting-edge AI implementation and experimental technology adoption' : 'proven AI integration and established technology frameworks'}, and ${learnerInitiative.label === 'Driver Learner' ? 'collaborative learning environments and team-based development' : 'independent learning pathways and self-directed skill acquisition'}, while benefiting from targeted development in ${focusAreas.toLowerCase()}.`;
};

// Generate label glossary
const generateLabelGlossary = () => {
  return [
    { label: 'Structured Analyst', definition: 'Uses stepwise, framework-driven reasoning. Reliable for structured problem-solving.' },
    { label: 'Holistic/Intuitive Thinker', definition: 'Connects ideas broadly, prefers insight-driven solutions.' },
    { label: 'Depth Learner', definition: 'Dives deep into fewer subjects, building mastery.' },
    { label: 'Breadth Learner', definition: 'Explores widely across topics, connects diverse concepts.' },
    { label: 'Independent Learner', definition: 'Learns autonomously, prefers self-direction.' },
    { label: 'Driver Learner', definition: 'Mobilizes peers, initiates team learning sprints.' },
    { label: 'Open Collaborator', definition: 'Proactive communicator, invites feedback, transparent.' },
    { label: 'Reserved Autonomist', definition: 'Prefers autonomy, shares updates thoughtfully but less often.' },
    { label: 'AI Innovator', definition: 'Experiments early with AI, integrates into workflows.' },
    { label: 'AI Adopter', definition: 'Prefers structured adoption of proven AI practices.' }
  ];
};

// Helper function to get working style descriptions
const getWorkingStyleDescription = (label) => {
  const workingStyles = {
    'Structured Analyst': 'Prefers step-by-step approaches, systematic documentation, and clear frameworks.',
    'Holistic/Intuitive Thinker': 'Connects diverse concepts, comfortable with ambiguity, seeks creative solutions.',
    'Depth Learner': 'Focuses intensively on mastering specific areas before moving to new topics.',
    'Breadth Learner': 'Explores multiple domains simultaneously, builds connections across fields.',
    'Driver Learner': 'Organizes team learning, shares knowledge proactively, leads study groups.',
    'Independent Learner': 'Self-directed learning pace, prefers individual research and exploration.',
    'Open Collaborator': 'Frequent communication, seeks feedback actively, transparent about progress.',
    'Reserved Autonomist': 'Works independently, provides thoughtful updates, values focused work time.',
    'AI Innovator': 'Early adopter of AI tools, experiments with new capabilities, integrates AI into workflows.',
    'AI Adopter': 'Careful evaluation of AI tools, prefers proven solutions, systematic implementation.'
  };
  
  return workingStyles[label] || 'Working style varies based on context and requirements.';
};

// Generate simplified AI content based on new rules
// Generate simplified AI content using local Express API
const generateSimplifiedAIContent = async (candidateData, analysis, role) => {
  try {
    // Use the local Express API
    const apiUrl = getApiBaseUrl();
    
    const response = await fetch(`${apiUrl}/api/analysis`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        candidateData,
        analysis,
        role
      })
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.error || 'Unknown API error');
    }

  } catch (error) {
    console.error('AI content generation error:', error);
    return null;
  }
};
// Update the main analysis function to use simplified approach
const analyzeCandidate = async (email, role, examCodes) => {
  try {
    // Validate inputs
    if (!email || !examCodes || examCodes.length === 0) {
      throw new Error('Email and exam codes are required');
    }

    if (examCodes.length > 3) {
      throw new Error('Maximum 3 exam codes are allowed per analysis');
    }

    // Fetch real data from Firebase
    const assessmentData = await fetchAssessmentDataFromFirebase(email, examCodes);

    // Process the data according to analysis rules
    const analysis = processAssessmentData(assessmentData, email, role, examCodes);

    // Generate simplified AI content
    const aiContent = await generateSimplifiedAIContent(assessmentData, analysis, role);

    if (aiContent) {
      // Update with simplified AI content
      analysis.mcqInterpretations = aiContent.mcqInterpretations;
      analysis.codingReviewInterpretation = aiContent.codingReviewInterpretation;
      analysis.psychometricResults = aiContent.psychometricResults;
      analysis.strengthsAndFocus = aiContent.strengthsAndFocus;
      analysis.suggestedInterviewQuestions = aiContent.suggestedInterviewQuestions;
      analysis.overallSummary = aiContent.overallSummary;
    }

    return analysis;

  } catch (error) {
    console.error('Candidate analysis error:', error);
    throw error;
  }
};

// Create fallback evaluation by calling AIEvaluationService.evaluateSubmission directly
const createFallbackEvaluation = async (submissionData, examData, examType) => {
  try {
    // Transform exam data to match the structure expected by AIEvaluationService
    const transformedExamData = {
      ...examData,
      sections: examData.sections.map(section => ({
        ...section,
        questions: (section.q || section.questions || []).map(question => ({
          ...question,
          // Ensure options array exists for MCQ questions and map shortened field names
          options: (question.options || question.opts || []).map(opt => ({
            id: opt.id,
            text: opt.t || opt.text,
            marks: opt.m !== undefined ? opt.m : (opt.marks || 0),
            isCorrect: opt.c !== undefined ? opt.c : (opt.isCorrect || false)
          }))
        }))
      }))
    };
    
    
    // Debug: Log the first question structure to see what we're working with
    if (transformedExamData.sections.length > 0 && transformedExamData.sections[0].questions.length > 0) {
      const firstQuestion = transformedExamData.sections[0].questions[0];
      console.log(`🔍 First question structure:`, {
        id: firstQuestion.id,
        type: firstQuestion.type,
        hasOptions: !!firstQuestion.options,
        optionsLength: firstQuestion.options ? firstQuestion.options.length : 0,
        optionsStructure: firstQuestion.options ? firstQuestion.options.slice(0, 2) : 'No options'
      });
    }
    
    // Import AIEvaluationService to use the exact same function
    const AIEvaluationService = (await import('./AIEvaluationService')).default;
    
    // Call the EXACT same function that DynamicExamScreen uses
    const evaluationResult = await AIEvaluationService.evaluateSubmission(transformedExamData, submissionData);
    
    // Add fallback markers to indicate this was created due to AI failure
    const fallbackEvaluation = {
      ...evaluationResult,
      fallback: true,
      note: "Fallback evaluation created using AIEvaluationService.evaluateSubmission due to AI evaluation failure"
    };
    
    return fallbackEvaluation;
    
  } catch (error) {
    console.error('Error creating fallback evaluation:', error);
    return null;
  }
};

// Debug function to check Firebase data structure
const debugFirebaseData = async (email, examCodes) => {
  console.log(`🔍 DEBUG: Checking Firebase data for ${email} with exam codes: ${examCodes.join(', ')}`);
  
  for (const examCode of examCodes) {
    console.log(`\n📋 Checking exam: ${examCode}`);
    
    // Check exam structure
    const examRef = firebase.database().ref(`TestPapers/${examCode}`);
    const examSnapshot = await examRef.once('value');
    console.log(`📊 Exam structure exists: ${examSnapshot.exists()}`);
    
    if (examSnapshot.exists()) {
      const examData = examSnapshot.val();
      console.log(`📝 Exam title: ${examData.title || examData.examTitle || 'No title'}`);
      console.log(`📚 Sections: ${examData.sections ? examData.sections.length : 0}`);
      if (examData.sections) {
        examData.sections.forEach((section, index) => {
          console.log(`  Section ${index + 1}: "${section.t || 'Untitled'}" (${section.q ? section.q.length : 0} questions)`);
        });
      }
    }
    
    // Check result data
    const resultRef = firebase.database().ref(`Exam_results/${examCode}/${email}`);
    const resultSnapshot = await resultRef.once('value');
    console.log(`📈 Result data exists: ${resultSnapshot.exists()}`);
    
    if (resultSnapshot.exists()) {
      const resultData = resultSnapshot.val();
      console.log(`👤 Candidate name: ${resultData.name || 'Not found'}`);
      console.log(`📅 Submitted at: ${resultData.submittedAt || 'Not found'}`);
      console.log(`🤖 AI Evaluation exists: ${!!resultData.aiEvaluation}`);
      console.log(`📝 Answers exist: ${!!resultData.answers}`);
      
      if (resultData.aiEvaluation && resultData.aiEvaluation.evaluations) {
        const evaluations = resultData.aiEvaluation.evaluations;
        console.log(`🔢 Evaluation count: ${Object.keys(evaluations).length}`);
        console.log(`📊 Sample evaluations:`, Object.entries(evaluations).slice(0, 3));
      }
    }
  }
};

const CandidateAnalysisService = {
  analyzeCandidate,
  debugFirebaseData
};

export default CandidateAnalysisService;
